import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { ChangeEvent, useState } from "react";
import { FieldErrors, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { toast } from "sonner";
import { useInstructionTemplates } from "@/hooks/useInstructionTemplates";
import { INSTRUCTION_MIN_LENGTH, INSTRUCTION_MAX_LENGTH, SingleLeadFormData } from "./services";

interface InstructionsProps {
    // Instruction data
    manualCallInstruction: string;
    setManualCallInstruction: (instruction: string) => void;
    
    // Form data
    activeTab: string;
    isSubmitting: boolean;
    parsedLeads: any[];
    singleErrors: FieldErrors<SingleLeadFormData>;
    setSingleValue: UseFormSetValue<SingleLeadFormData>;
    triggerSingle: UseFormTrigger<SingleLeadFormData>;
}

const Instructions = ({
    manualCallInstruction,
    setManualCallInstruction,
    activeTab,
    isSubmitting,
    parsedLeads,
    singleErrors,
    setSingleValue,
    triggerSingle
}: InstructionsProps) => {
  const {
      templates,
      templatesLoading,
      saveTemplate,
      deleteTemplate,
      isSaving,
      isDeleting
  } = useInstructionTemplates(true);
    
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [originalInstruction, setOriginalInstructionLocal] = useState<string>('');
    
    const isInstructionModified = manualCallInstruction.trim() !== originalInstruction.trim();

    const handleInstructionChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const instruction = event.target.value;
        setManualCallInstruction(instruction);
        setSelectedTemplateId(null);
        if (activeTab === 'single') {
            setSingleValue('manual_call_instruction', instruction, { shouldValidate: true });
        }
    };

    return (
        <div className="space-y-1.5 col-span-2">
            <div className="flex justify-between items-center min-h-[36px]">
                <Label htmlFor="manual-call-instruction">What should the AI call about?</Label>
                {templates.length > 0 && (
                    <Select 
                        value={selectedTemplateId || ""} 
                        onValueChange={(val) => {
                            const found = templates.find(t => t.id === val);
                            if (found) {
                                setManualCallInstruction(found.instruction);
                                setSelectedTemplateId(val);
                                setOriginalInstructionLocal(found.instruction); // Set as new baseline
                                if (activeTab === 'single') { // Also update single form state if active
                                    setSingleValue('manual_call_instruction', found.instruction, { shouldValidate: true });
                                }
                            }
                        }}
                    >
                        <SelectTrigger className="w-40" disabled={templatesLoading}>
                            <SelectValue placeholder="Use Template">
                                {selectedTemplateId && templates.find(t => t.id === selectedTemplateId)?.template_name}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="w-full">
                            {templates.map(t => (
                                <div key={t.id} className="relative">
                                    <SelectItem 
                                        value={t.id} 
                                        className="w-full cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground pr-8"
                                    >
                                        {t.template_name}
                                    </SelectItem>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-1 top-1/2 -translate-y-1/2 size-5 p-0 hover:bg-red-100 hover:text-red-600 opacity-70 hover:opacity-100 z-10"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (confirm(`Delete template "${t.template_name}"?`)) {
                                                deleteTemplate(t.id);
                                            }
                                        }}
                                        disabled={isDeleting}
                                    >
                                        <Trash2 className="size-3" />
                                    </Button>
                                </div>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
            <Textarea
                id="manual-call-instruction"
                value={manualCallInstruction}
                onChange={handleInstructionChange}
                placeholder="e.g., 'Call to confirm their interest in our new premium package and offer to schedule a demo.' or 'Follow up on the quote we sent last Tuesday.'"
                className="mt-1"
                rows={8}
                disabled={isSubmitting && (parsedLeads.length > 0 || activeTab === 'single')} 
                maxLength={INSTRUCTION_MAX_LENGTH}
                onBlur={() => { 
                    if(activeTab === 'single') triggerSingle('manual_call_instruction');
                }}
            />
            <div className="flex justify-between items-center">
                {activeTab === 'single' && singleErrors.manual_call_instruction ? (
                    <p className="text-red-600 text-xs">{singleErrors.manual_call_instruction.message}</p>
                ) : <div />} 
                <p className={`text-xs ${manualCallInstruction.length > INSTRUCTION_MAX_LENGTH ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {manualCallInstruction.length}/{INSTRUCTION_MAX_LENGTH}
                </p>
            </div>
            {isInstructionModified ? (
                <div className="flex">
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                        if (manualCallInstruction.trim().length < INSTRUCTION_MIN_LENGTH) {
                            toast.error(`Instruction too short to save as template (min ${INSTRUCTION_MIN_LENGTH} characters).`);
                            return;
                        }
                        const name = prompt('Enter a name for this template:');
                        if (name && name.trim()) {
                            saveTemplate({ templateName: name.trim(), instruction: manualCallInstruction.trim() });
                            setOriginalInstructionLocal(manualCallInstruction.trim()); // Update baseline after saving
                        }
                    }} disabled={isSaving || manualCallInstruction.trim().length < INSTRUCTION_MIN_LENGTH}>
                        Save as Template
                    </Button>
                </div>
            ): (
                <div className="h-[32px] w-full">

                </div>
            )}
        </div>
    );
};

export default Instructions;